// Deferred start-up for heavy below-the-fold components (sliders, video players).
//
// main.ts imports every component eagerly, so each one's set-up (Swiper,
// Plyr, ...) runs during page start-up even when the component sits far below
// the fold. For the components listed here that set-up is deferred until the
// component is about to be seen, the same way Edge Delivery Services loads
// its blocks:
//   - near the viewport when the page starts (e.g. a media-center page, or a
//     reload half way down) -> started straight away, exactly as before;
//   - otherwise -> started once it comes within one screen of the viewport
//     (or, if it is hidden when the page starts, in idle time after load).
// If someone manages to click inside a component before it has started (it
// is normally started a full screen before it is visible), the click is held,
// the component is started and the same click is then replayed.
//
// Nothing else changes: the markup is still server-rendered (so SEO and
// no-JS content are unaffected), the CSS is still in the main stylesheet, and
// the code still ships inside this bundle - `webpackMode: "eager"` only delays
// running the module, it never makes a network request. These modules are
// excluded from the component glob in main.ts; every other component loads
// as before.
//
// To add a component: exclude its folder from the glob in main.ts and add an
// entry below. Only use this for components whose own start-up only touches
// their own root element and that start from jQuery ready / module load
// (not from DOMContentLoaded or window "load", which have already fired).
import $ from "jquery";
import { ScrollTrigger } from "gsap/ScrollTrigger";

const LAZY_COMPONENTS = [
    {
        selector: ".seasonal__banner",
        load: () => import(/* webpackMode: "eager" */ "../../chli-components/seasonalBanner/seasonalBanner"),
    },
    {
        selector: ".blogstories",
        load: () => import(/* webpackMode: "eager" */ "../../chli-components/blogstories/blogstories"),
    },
    {
        selector: ".whatIsLifeInsurance__section",
        load: () => import(/* webpackMode: "eager" */ "../../chli-components/whatisLifeInsurance/whatisLifeInsurance"),
    },
    {
        selector: ".media_center",
        load: () => import(/* webpackMode: "eager" */ "../../chli-components/mediaCenter/mediaCenter"),
    },
];

const pending = [];

// Resolves once the component's own jQuery ready callbacks have run: they
// were queued while its module ran, so a ready callback queued after the
// import settles runs after them.
function start(entry) {
    if (!entry.started) {
        entry.started = entry.load().then(() => new Promise((resolve) => $(() => resolve())));
        entry.started.then(() => refreshScrollTriggersAfter(entry));
        if (entry.observer) {
            entry.observer.disconnect();
        }
        const i = pending.indexOf(entry);
        if (i !== -1) {
            pending.splice(i, 1);
        }
    }
    return entry.started;
}

// Scroll animations measure where their trigger sits on the page. If a
// component above one of them starts later and changes height, re-measure,
// as GSAP does itself after window "load". Components below a trigger cannot
// move it, so nothing is done for them.
function refreshScrollTriggersAfter(entry) {
    const above = ScrollTrigger.getAll().some((trigger) =>
        trigger.trigger instanceof Element &&
        entry.roots.some((root) => root !== trigger.trigger && !root.contains(trigger.trigger) &&
            root.compareDocumentPosition(trigger.trigger) === Node.DOCUMENT_POSITION_FOLLOWING)
    );
    if (above) {
        ScrollTrigger.refresh();
    }
}

function isNearViewport(rect) {
    const height = window.innerHeight;
    return rect.bottom > -height && rect.top < height * 2;
}

// Hidden when the page starts (e.g. inside a closed tab or a breakpoint-only
// wrapper): start it in idle time after the page has loaded, so it is ready
// before it can be shown, as it was when it started with the page.
function startWhenIdle(entry) {
    const run = () => ("requestIdleCallback" in window
        ? window.requestIdleCallback(() => start(entry), { timeout: 3000 })
        : window.setTimeout(() => start(entry), 200));
    if (document.readyState === "complete") {
        run();
    } else {
        window.addEventListener("load", run, { once: true });
    }
}

LAZY_COMPONENTS.forEach((entry) => {
    entry.roots = Array.prototype.slice.call(document.querySelectorAll(entry.selector));
    if (!entry.roots.length) {
        return;
    }
    const rects = entry.roots.map((root) => root.getBoundingClientRect());
    if (!("IntersectionObserver" in window) || rects.some(isNearViewport)) {
        start(entry);
        return;
    }
    pending.push(entry);
    if (rects.some((rect) => !rect.width && !rect.height)) {
        startWhenIdle(entry);
    }
    entry.observer = new IntersectionObserver((records) => {
        if (records.some((record) => record.isIntersecting)) {
            start(entry);
        }
    }, { rootMargin: "100% 0px" });
    entry.roots.forEach((root) => entry.observer.observe(root));
});

if (pending.length) {
    // Back-up for jumps (End key, anchor links, a fast fling) that carry a
    // component past the observed zone between two frames: once the reader has
    // reached or passed it, start it.
    let queued = false;
    const checkPassed = () => {
        queued = false;
        pending.slice().forEach((entry) => {
            if (entry.roots.some((root) => {
                const rect = root.getBoundingClientRect();
                return (rect.width || rect.height) && rect.top < window.innerHeight * 2;
            })) {
                start(entry);
            }
        });
        if (!pending.length) {
            window.removeEventListener("scroll", onScroll);
        }
    };
    const onScroll = () => {
        if (!queued) {
            queued = true;
            window.requestAnimationFrame(checkPassed);
        }
    };
    window.addEventListener("scroll", onScroll, { passive: true });

    document.addEventListener("click", function holdClick(event) {
        const target = event.target;
        const entry = target instanceof Element && pending.find((item) =>
            item.roots.some((root) => root.contains(target))
        );
        if (!entry) {
            if (!pending.length) {
                document.removeEventListener("click", holdClick, true);
            }
            return;
        }
        event.preventDefault();
        event.stopImmediatePropagation();
        start(entry).then(() => {
            target.dispatchEvent(new MouseEvent("click", { bubbles: true, cancelable: true, view: window }));
        });
    }, true);
}
